import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Send, MessageSquare, ArrowLeft, Home, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

// URL du logo officiel BabiDoc
const LOGO_URL = "https://customer-assets.emergentagent.com/job_babidoc-health/artifacts/7a0rukid_HUMMM0fc84adc-81cc-4751-9a5e-2c9327247823.png";

const CATEGORIES = [
  { value: 'general', label: 'Avis Général' },
  { value: 'service', label: 'Qualité du Service' },
  { value: 'app', label: 'Application / Site Web' },
  { value: 'suggestion', label: 'Suggestion d\'amélioration' }
];

const FeedbackPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [category, setCategory] = useState('general');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error('Veuillez sélectionner une note');
      return;
    }
    
    if (!comment.trim()) {
      toast.error('Veuillez écrire un commentaire');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/feedback`, {
        rating,
        comment,
        category
      });
      setSubmitted(true);
      toast.success('Merci pour votre avis !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  // Redirect to login if not authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-green-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Connexion Requise</h2>
            <p className="text-gray-600 mb-6">Vous devez être connecté pour laisser un avis.</p>
            <Button onClick={() => navigate('/login')} className="btn-primary">
              Se connecter
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-green-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Merci pour votre avis !</h2>
            <p className="text-gray-600 mb-6">
              Votre retour est précieux et nous aide à améliorer BabiDoc.
            </p>
            <div className="flex justify-center space-x-2 mb-6">
              {[...Array(rating)].map((_, i) => (
                <Star key={i} className="w-8 h-8 text-yellow-400 fill-current" />
              ))}
            </div>
            <Button onClick={() => navigate('/')} className="btn-primary">
              Retour à l'accueil
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-green-50 py-8 px-4" data-testid="feedback-page">
      {/* Back button */}
      <button
        onClick={() => navigate('/')}
        className="fixed top-4 left-4 flex items-center space-x-2 text-gray-600 hover:text-teal-600 transition-colors z-10"
        data-testid="back-home-btn"
      >
        <ArrowLeft className="w-6 h-6" />
        <Home className="w-5 h-5" />
        <span className="text-sm font-medium">Accueil</span>
      </button>

      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <img 
              src={LOGO_URL} 
              alt="BabiDoc" 
              className="w-16 h-16 object-cover rounded-full"
            />
            <h1 className="text-3xl font-bold text-teal-700">BabiDoc</h1>
          </div>
          <h2 className="text-xl text-gray-700">Votre avis compte pour nous</h2>
        </div>

        <Card className="shadow-xl">
          <CardHeader className="text-center bg-gradient-to-r from-teal-50 to-green-50 rounded-t-lg">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center">
                <MessageSquare className="w-8 h-8 text-teal-600" />
              </div>
            </div>
            <CardTitle className="text-xl text-teal-900">
              Laisser un Avis
            </CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              Connecté en tant que: <strong>{user.full_name}</strong>
            </p>
          </CardHeader>

          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Rating */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Votre Note *
                </label>
                <div className="flex justify-center space-x-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="transition-transform hover:scale-110"
                      data-testid={`star-${star}`}
                    >
                      <Star
                        className={`w-10 h-10 ${
                          star <= (hoverRating || rating)
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                <p className="text-center text-sm text-gray-500 mt-2">
                  {rating === 0 && 'Cliquez pour noter'}
                  {rating === 1 && 'Très insatisfait'}
                  {rating === 2 && 'Insatisfait'}
                  {rating === 3 && 'Correct'}
                  {rating === 4 && 'Satisfait'}
                  {rating === 5 && 'Très satisfait'}
                </p>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Catégorie
                </label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="w-full" data-testid="category-select">
                    <SelectValue placeholder="Choisir une catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Comment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Votre Commentaire *
                </label>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Partagez votre expérience avec BabiDoc..."
                  rows={5}
                  className="w-full"
                  data-testid="comment-input"
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading || rating === 0 || !comment.trim()}
                className="w-full btn-primary h-12 text-base"
                data-testid="submit-feedback-btn"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Envoyer mon avis
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-gray-500 mt-6">
          Votre avis nous aide à améliorer nos services. Merci de votre confiance !
        </p>
      </div>
    </div>
  );
};

export default FeedbackPage;
